import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Empty, Select, SelectProps, Spin, Typography } from 'antd'
import { FieldError } from 'react-hook-form'
import ErrorText from 'src/app/shared/components/common/error-text'
import './index.scss'
import lodash, { isArray } from 'lodash'
import { DefaultOptionType } from 'antd/es/select'

interface OptionProps {
  label: string
  value: any
}

const CustomSelect: React.FC<
  SelectProps & {
    label?: string
    required?: boolean
    error?: FieldError
    containerStyle?: React.CSSProperties
    haveAllOption?: boolean // select have "All" option in dropdown
    sortWithAll?: boolean
  }
> = (props) => {
  const {
    label,
    size = 'large',
    required,
    error,
    style,
    containerStyle,
    loading,
    options,
    value,
    haveAllOption,
    mode,
    filterOption,
    sortWithAll,
    onChange,
    onClick,
    onDropdownVisibleChange,
    ...etc
  } = props
  const [open, setOpen] = useState<boolean | undefined>(haveAllOption ? false : undefined) // NOTE: "open" state is only used when having haveAllOption
  const selectMode = useMemo(() => {
    if (!haveAllOption) {
      // all cases not having "Select All" in dropdown
      return mode
    }
    // cases having "Select All" in dropdown
    if (value) {
      if (isArray(value) && value.includes(null)) {
        return undefined
      }
      return 'multiple'
    }
    return undefined
  }, [haveAllOption, mode, value])

  const handleSelect = useCallback(
    (value: string | Array<string | null>, option: OptionProps | Array<OptionProps>) => {
      if (haveAllOption) {
        if (value === null || (isArray(value) && value.includes(null))) {
          onChange?.(null, option)
        } else {
          !isArray(value) ? onChange?.([value], option) : onChange?.(value, option)
        }
      } else {
        onChange?.(value, option)
      }
    },
    [haveAllOption, onChange],
  )

  const customFilterOption = useCallback((inputValue: string, option: any) => {
    return option?.label?.toLowerCase().includes(inputValue.toLowerCase())
  }, [])

  const sortWithAllOption = useCallback((options: Array<DefaultOptionType> | undefined) => {
    return options?.sort((a, b) => {
      // NOTE: move all option to the top of the list
      if (a.value === null || b.value === null) {
        return a.label ? 1 : -1
      }
      return (a.label as string).localeCompare(b.label as string)
    })
  }, [])

  useEffect(() => {
    // NOTE: only change "open" state when having haveAllOption property
    // normal select doesnt need "open" state
    if (haveAllOption && selectMode === undefined) {
      setOpen(false)
    }
  }, [haveAllOption, selectMode])

  return (
    <div
      className='custom-select'
      style={{
        flex: 1,
        ...containerStyle,
      }}
    >
      <Typography.Text
        style={{ textAlign: 'left', fontSize: '14px', fontWeight: 700 }}
        className={`${required ? 'required' : ''} custom-select-label`}
      >
        {label}
      </Typography.Text>
      <Select
        style={{
          width: '100%',
          marginBottom: error ? 8 : 0,
          marginTop: 4,
          ...style,
        }}
        filterOption={filterOption ?? customFilterOption}
        size={size}
        status={!!error ? 'error' : ''}
        notFoundContent={
          loading ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
              }}
            >
              <Spin />
            </div>
          ) : (
            <Empty />
          )
        }
        loading={loading}
        options={haveAllOption || sortWithAll ? sortWithAllOption(options) : lodash.sortBy(options, 'label', 'desc')}
        mode={selectMode}
        value={value}
        onChange={handleSelect as any}
        open={open}
        onClick={(event) => {
          if (haveAllOption) {
            setOpen(true)
          }
          onClick?.(event)
        }}
        onDropdownVisibleChange={(visible) => {
          if (haveAllOption) {
            setOpen(visible)
          }
          onDropdownVisibleChange?.(visible)
        }}
        {...etc}
      />
      {error && <ErrorText>{error.message}</ErrorText>}
    </div>
  )
}

export default CustomSelect
